//@flow
import React, {useEffect, useMemo, useState} from 'react';

import {makeStyles, Typography, Grid, MenuItem} from '@material-ui/core';
import CustomTextField from '../../../../../../../elements/CustomTextField/CustomTextField';
import PhoneInput from '../../../../../../../elements/PhoneInput';
import {colors} from '../../../../../../../assets/styles/colors';
import CustomButton from '../../../../../../../elements/CustomButton/CustomButton';
import {useForm} from 'react-hook-form';
import {openConversationService} from '../../../../promises/conversation';
import {formatStoreDeviceIdentifier, useStoreDevices} from '../../../../utils';
import CustomSelect from '../../../../../../../elements/CustomSelect/CustomSelect';
import {useAlert} from '../../../../../../../elements/Alert/AlertContext';
import {useStoreCountry} from '../../../../../../country/utils';
import {sanitizePhoneNumber} from '../../../../../../../utils/phone';
import {CircularProgress} from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: '400px',
    position: 'relative',
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.8)',
    zIndex: 1,
    top: 0,
    left: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
  },
  title: {
    fontWeight: 500,
    fontSize: '1rem',
    color: colors.grey800,
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
  },
}));

const CONVERSATION_FORM_FIELDS = {
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  IDENTIFIER: 'phoneNumber',
  MESSAGE: 'message',
};

type Props = {
  onSubmitSuccess: () => void,
  defaultValue?: {
    firstName?: string,
    lastName?: string,
    phoneNumber?: string,
    message?: string,
  },
};

function getErrorMessage(error: string) {
  if (error === 'CrmMessagingContactAlreadyExistError') {
    return 'El contacto ya existe';
  }

  return 'Ocurrió un error';
}

export default function OpenConversationForm(props: Props) {
  const {onSubmitSuccess, defaultValue} = props;
  const classes = useStyles();

  const {register, handleSubmit, errors, setValue, watch} = useForm({
    defaultValues: {
      [CONVERSATION_FORM_FIELDS.FIRST_NAME]: defaultValue?.firstName || '',
      [CONVERSATION_FORM_FIELDS.LAST_NAME]: defaultValue?.lastName || '',
      [CONVERSATION_FORM_FIELDS.IDENTIFIER]: defaultValue?.phoneNumber || '',
      [CONVERSATION_FORM_FIELDS.MESSAGE]: defaultValue?.message || '',
    },
  });
  const {storeDevices} = useStoreDevices();
  const country = useStoreCountry();
  const {showAlert} = useAlert();

  const watchIdentifier = watch(CONVERSATION_FORM_FIELDS.IDENTIFIER);

  const [deviceSelected, setDeviceSelected] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const connectedWhatsappDevices = useMemo(
    () =>
      storeDevices.filter(
        device =>
          device.state === 'CONNECTED' && device.service === 'WHATSAPP_WEB'
      ),
    [storeDevices]
  );

  useEffect(() => {
    register(CONVERSATION_FORM_FIELDS.IDENTIFIER, {
      required: 'Este campo es requerido',
    });
  }, [register]);

  const handleInputPhoneChange = (phone: string) => {
    setValue(CONVERSATION_FORM_FIELDS.IDENTIFIER, phone);
  };

  const onSubmit = data => {
    if (!deviceSelected) {
      showAlert('Selecciona un dispositivo', {
        type: 'warning',
      });
      return;
    }

    setIsSubmitting(true);
    openConversationService({
      contactInformation: {
        fullName: `${data[CONVERSATION_FORM_FIELDS.FIRST_NAME]} ${
          data[CONVERSATION_FORM_FIELDS.LAST_NAME]
        }`,
      },
      message: {
        text: data[CONVERSATION_FORM_FIELDS.MESSAGE],
      },
      deviceId: deviceSelected,
      identifier: sanitizePhoneNumber(
        data[CONVERSATION_FORM_FIELDS.IDENTIFIER]
      ),
      provider: 'WHATSAPP_WEB',
    })
      .then(() => {
        onSubmitSuccess();
      })
      .catch(error => {
        console.log(error);
        const message = getErrorMessage(error?.error?.name);
        showAlert(message, {
          type: 'error',
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  useEffect(() => {
    if (connectedWhatsappDevices.length === 0) {
      setDeviceSelected('');
      return;
    }

    const stillValid = connectedWhatsappDevices.some(
      device => device.id === deviceSelected
    );
    if (stillValid) return;

    if (connectedWhatsappDevices.length === 1) {
      setDeviceSelected(connectedWhatsappDevices[0].id);
      return;
    }

    setDeviceSelected('');
  }, [connectedWhatsappDevices, deviceSelected]);

  return (
    <section className={classes.root}>
      {isSubmitting && (
        <div className={classes.loader}>
          <CircularProgress size={32} color="primary" />
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography className={classes.title}>
              Nueva Conversación
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <CustomSelect
              fullWidth
              value={deviceSelected}
              onChange={e => {
                setDeviceSelected(e.target.value);
              }}
              label="Dispositivo"
              disabled={connectedWhatsappDevices.length === 0}
            >
              {connectedWhatsappDevices.map(device => (
                <MenuItem key={device.id} value={device.id}>
                  <Typography>{formatStoreDeviceIdentifier(device)}</Typography>
                </MenuItem>
              ))}
            </CustomSelect>
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              label="Nombre"
              name={CONVERSATION_FORM_FIELDS.FIRST_NAME}
              inputRef={register({
                required: 'Este campo es requerido',
              })}
              helperText={errors[CONVERSATION_FORM_FIELDS.FIRST_NAME]?.message}
              error={!!errors[CONVERSATION_FORM_FIELDS.FIRST_NAME]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              label="Apellido"
              name={CONVERSATION_FORM_FIELDS.LAST_NAME}
              inputRef={register({
                required: 'Este campo es requerido',
              })}
              helperText={errors[CONVERSATION_FORM_FIELDS.LAST_NAME]?.message}
              error={!!errors[CONVERSATION_FORM_FIELDS.LAST_NAME]}
            />
          </Grid>
          <Grid item xs={12}>
            <PhoneInput
              defaultCountry={country}
              label="Número de teléfono"
              name="identifier"
              value={watchIdentifier}
              onChange={handleInputPhoneChange}
              error={!!errors[CONVERSATION_FORM_FIELDS.IDENTIFIER]}
              helperText={errors[CONVERSATION_FORM_FIELDS.IDENTIFIER]?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              name={CONVERSATION_FORM_FIELDS.MESSAGE}
              label="Escribe un mensaje"
              multiline
              rows={4}
              inputRef={register({
                required: 'Este campo es requerido',
              })}
              helperText={errors[CONVERSATION_FORM_FIELDS.MESSAGE]?.message}
              error={!!errors[CONVERSATION_FORM_FIELDS.MESSAGE]}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomButton
              variant="contained"
              fullWidth
              color="primary"
              type="submit"
              disabled={isSubmitting || !deviceSelected}
            >
              {isSubmitting ? 'Enviando...' : 'Conversar'}
            </CustomButton>
          </Grid>
        </Grid>
      </form>
    </section>
  );
}
